/**
 * Modelos do contrato OTP (reset de senha / primeiro acesso por código).
 *
 * Fonte da verdade: `.brain/tecnologia/features/reset-senha-otp/spec-tecnica.md`
 * §4.9, §5.1-§5.5. Os dois fluxos (`esqueci-senha` e `primeiro-acesso`)
 * compartilham o mesmo shape — só o prefixo de rota e o nome do campo de
 * senha final divergem (ver `OtpAuthService`).
 */

/** Fluxo público que usa o desafio OTP — define o prefixo de rota. */
export type OtpFluxo = 'esqueci-senha' | 'primeiro-acesso';

/**
 * Item da lista de desambiguação de empresa (ramo 2..5 candidatos).
 *
 * `destinoMascarado` é o rótulo de escolha (M-1, spec §4.6) — NUNCA a razão
 * social. `nome` só vem preenchido pelo backend quando duas máscaras
 * colidem (desempate); o front não decide isso, só renderiza o que vier.
 */
export interface OtpEmpresaCandidata {
  ref: string;
  destinoMascarado: string;
  nome?: string;
}

/** Corpo de `POST .../solicitar-codigo` quando 0 ou 1 candidato (spec §5.1). */
export interface SolicitarCodigoNormal {
  desafioId: string;
  expiraEmSegundos: number;
  reenvioPermitidoEmSegundos: number;
}

/** Corpo de `POST .../solicitar-codigo` quando 2..5 candidatos (spec §5.1). */
export interface SolicitarCodigoAmbiguo {
  desafioId: string;
  escolhaEmpresaEmSegundos: number;
  empresas: OtpEmpresaCandidata[];
}

export type SolicitarCodigoResponse = SolicitarCodigoNormal | SolicitarCodigoAmbiguo;

/**
 * Ramifica pela presença de `empresas`, nunca por status HTTP — o `200`
 * ambíguo não é erro (spec §5.1: "Não é 409. Nada falhou").
 */
export function isSolicitarCodigoAmbiguo(
  resp: SolicitarCodigoResponse
): resp is SolicitarCodigoAmbiguo {
  return Array.isArray((resp as SolicitarCodigoAmbiguo).empresas);
}

/** Corpo de `POST .../reenviar-codigo` (spec §5.2). */
export interface ReenviarCodigoResponse {
  expiraEmSegundos: number;
  reenvioPermitidoEmSegundos: number;
}

/** Corpo de `POST .../validar-codigo` (spec §5.3) — comum aos dois fluxos. */
export interface ValidarCodigoResponse {
  resetToken: string;
  destinoMascarado: string;
  /** Só existe no fluxo `primeiro-acesso`. */
  primeiroNome?: string;
}

/** Corpo de `POST .../criar-senha` do `primeiro-acesso` (spec §5.5). */
export interface CriarSenhaResponse {
  usuarioId: number;
  aceiteTermosToken?: string;
}

/** Envelope padrão do backend Manager. */
export interface OtpEnvelope<T> {
  conteudo: T;
}

/** Códigos de erro do contrato OTP (spec §5, `MANAGER.AUTH.*`). */
export const OTP_ERROR_CODES = {
  ENTRADA_INVALIDA: 'MANAGER.AUTH.ENTRADA_INVALIDA',
  ESCOLHA_INVALIDA: 'MANAGER.AUTH.OTP_ESCOLHA_INVALIDA',
  CODIGO_INVALIDO: 'MANAGER.AUTH.OTP_CODIGO_INVALIDO',
  REENVIO_INDISPONIVEL: 'MANAGER.AUTH.OTP_REENVIO_INDISPONIVEL',
  TOKEN_INVALIDO: 'MANAGER.AUTH.OTP_TOKEN_INVALIDO',
  RECUPERACAO_SENHA_INVALIDA: 'MANAGER.AUTH.RECUPERACAO_SENHA_INVALIDA',
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
  /** Só emitido em `primeiro-acesso` (spec §4.6-B) — `esqueci-senha` nunca recebe este código. */
  CONTA_JA_POSSUI_SENHA: 'MANAGER.AUTH.CONTA_JA_POSSUI_SENHA',
  /**
   * `403` em `validar-codigo` (spec §4.6-C) — conta resolvida com
   * `acessa_portal = false`. Reusa a string exata do login
   * (`auth.controller.ts`), sem prefixo `MANAGER.AUTH.` — decisão explícita
   * da spec, herdada do contrato pré-existente do login.
   */
  ACESSO_PORTAL_NEGADO: 'ACESSO_PORTAL_NEGADO'
} as const;

/**
 * Valor digitado no passo 1 (identificador OU e-mail — D9), propagado pelo
 * redirecionamento `primeiro-acesso` → `esqueci-senha` quando a conta já tem
 * senha cadastrada (spec §4.6-B / §12-C). Nunca os dois campos juntos.
 */
export interface OtpContaJaPossuiSenhaState {
  identificador?: string;
  email?: string;
  /** Mensagem do backend (`err.error.mensagem`) — exibida como toast no destino, explicando o redirecionamento. */
  mensagem: string;
}

/**
 * Chave do `state` do Router usada nesse redirecionamento — NUNCA query
 * param: identificador/e-mail é dado pessoal e não pode ir pra URL (fica no
 * histórico do navegador e em log de proxy — spec §12-C item 4).
 */
export const OTP_CONTA_JA_POSSUI_SENHA_STATE_KEY = 'otpContaJaPossuiSenha';

/**
 * `403 ACESSO_PORTAL_NEGADO` (spec §4.6-C) — o código validou, mas a conta
 * não tem acesso ao portal. Decisão do Marcos: em vez de voltar ao passo 1
 * do mesmo fluxo, volta pro login, com o toast explicando o motivo.
 */
export interface OtpAcessoPortalNegadoState {
  mensagem: string;
}

export const OTP_ACESSO_PORTAL_NEGADO_STATE_KEY = 'otpAcessoPortalNegado';
